import { useState } from "react";
import { api } from "../lib/api";
import { useApi } from "../lib/useApi";
import type { ConfigRow } from "../lib/types";
import { useAuth } from "../lib/auth";
import { formatDate } from "../lib/format";
import { Button, ErrorState, Input, PageHeader, Spinner, Table, Td } from "../components/ui";

export default function Config() {
  const { hasRole } = useAuth();
  const canWrite = hasRole("admin");
  const { data, loading, error, reload } = useApi<{ config: ConfigRow[] }>("/config");
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  async function save(key: string) {
    setBusy(key);
    setSaveError(null);
    try {
      await api.put(`/config/${encodeURIComponent(key)}`, { value: edits[key] });
      setEdits(({ [key]: _, ...rest }) => rest);
      reload();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  if (loading) return <Spinner />;
  if (error || !data) return <ErrorState message={error ?? "Failed to load"} onRetry={reload} />;

  return (
    <div>
      <PageHeader
        title="Config"
        subtitle="Runtime configuration (app_config in the free tools database) — MC limits, signing limits, tier constants"
      />
      {saveError && <ErrorState message={saveError} />}
      <div className="mt-3 rounded-lg border border-slate-200 bg-white shadow-sm">
        <Table headers={["Key", "Value", "Description", "Last updated", canWrite ? "" : null].filter((h) => h !== null)}>
          {data.config.map((row) => {
            const edited = edits[row.key] !== undefined && edits[row.key] !== row.value;
            return (
              <tr key={row.key}>
                <Td className="font-mono text-xs">{row.key}</Td>
                <Td className="w-40">
                  {canWrite ? (
                    <Input
                      value={edits[row.key] ?? row.value}
                      onChange={(e) => setEdits((prev) => ({ ...prev, [row.key]: e.target.value }))}
                      className="max-w-[10rem] font-mono text-xs"
                    />
                  ) : (
                    <span className="font-mono text-xs">{row.value}</span>
                  )}
                </Td>
                <Td className="text-xs text-slate-500">{row.description ?? "—"}</Td>
                <Td className="text-xs">
                  {formatDate(row.updated_at)}
                  {row.updated_by && <span className="block text-slate-400">{row.updated_by}</span>}
                </Td>
                {canWrite && (
                  <Td>
                    <Button size="sm" disabled={!edited || busy === row.key} onClick={() => save(row.key)}>
                      {busy === row.key ? "Saving…" : "Save"}
                    </Button>
                  </Td>
                )}
              </tr>
            );
          })}
        </Table>
      </div>
    </div>
  );
}
